import { dispatch_integration_types } from '@/components/MyOrders/filterOptions';
import { GENERAL_ELECTRIC } from '@/constants/integrationTypes';

export const FUEL_TYPE_OPTIONS = [
    { value: 'LOCAL_PADD', label: 'Local' },
    { value: 'NATIONAL_PADD', label: 'National' },
];

// clients.carrier_type — see migration 1777613055039_self_serve.
export const CARRIER_TYPE_LABELS = {
    AGENT: 'Agent',
    FLEX: 'Flex',
    LTL: 'LTL',
    GIG: 'Gig',
    PARTNER: 'Partner',
};

export const SHIPPER_TYPE_LABELS = {
    MARKETPLACE: 'Marketplace',
    AUTO_ASSIGN: 'Auto Assign',
    RATE_SHOP: 'Rate Shop',
};

// Display labels for pricing_overrides.marketplace_tier. T2_INTEGRATIONS_ONLY is a
// legacy value still possible on existing rows — render it as "Tier 2" but don't
// surface it as a pickable option (T2_INTEGRATIONS_BILLING is the canonical Tier 2).
export const MARKETPLACE_TIER_LABELS = {
    UNKNOWN: 'Unknown',
    T1_INTEGRATIONS_ONLY: 'Tier 1',
    T2_INTEGRATIONS_BILLING: 'Tier 2',
    T2_INTEGRATIONS_ONLY: 'Tier 2',
    T3_FULLY_MANAGED: 'Tier 3',
};

export const MARKETPLACE_TIER_OPTIONS = [
    { value: 'UNKNOWN', label: 'Unknown' },
    { value: 'T1_INTEGRATIONS_ONLY', label: 'Tier 1' },
    { value: 'T2_INTEGRATIONS_BILLING', label: 'Tier 2' },
    { value: 'T3_FULLY_MANAGED', label: 'Tier 3' },
];

// clients.supply_chain_legs (public.supply_chain_leg enum) — see migration 1777613055039_self_serve.
// Display labels intentionally diverge from the enum names (e.g. MIDDLE_MILE renders as "First")
// to match the operator-facing terminology in the Figma; don't infer the mapping from the names.
export const SUPPLY_CHAIN_LEG_LABELS = {
    FINAL_MILE: 'Final',
    MIDDLE_MILE: 'First',
    E2E: 'Full',
    WAREHOUSE: 'Crossdock',
    GIG: 'Gig',
    CONTRACTOR: 'Motor Carrier',
};

export const INTEGRATION_TYPE_LABELS = {
    ...Object.fromEntries(dispatch_integration_types.map(({ value, label }) => [value, label])),
    // PC Miler is shipper-scoped (Account Details → Mileage), not a dispatch integration, so it's
    // intentionally absent from dispatch_integration_types; its label is declared here for display.
    PC_MILER: 'PC Miler',
    // Custom Global is first-leg LTL and full-mile / last-mile (same as CEVA / Metro).
    CUSTOMCO: 'Custom Global',
    // Not in dispatch_integration_types, which would also add them to the orders page's TMS filter.
    VAULT: 'Vault',
    FORWARD_AIR: 'Forward Air',
};

// integration_overrides.type values that are shipper-scoped and configured through a dedicated
// surface (Account Details → Mileage) rather than as carrier/marketplace integrations. Excluded
// from the generic carrier-setup dropdown, and by buildCarrierAssetMaps from the carrier asset
// maps (otherwise they'd surface/count as marketplace integrations).
export const SHIPPER_SCOPED_OVERRIDE_TYPES = ['PC_MILER'];

// Each entry defines a chip. `events` is an array — the chip highlights if any of its
// event keys are present in the integration's subscriptions array.
export const SUBSCRIBED_EVENT_CHIPS = [
    { label: 'Agent Dispatched', events: ['carrier_assigned'] },
    { label: 'Order Cancelled', events: ['order_cancelled'] },
    { label: 'Order Updated', events: ['order_details_updated'] },
    { label: 'Reschedule Requested', events: ['order_scheduled', 'order_rescheduled'] },
];

// A hand-kept copy of the config keys each onward-functions client reads. Bungii and DispatchIt are absent:
// their clients read the rate override's pricing_parameters, not config.
export const INTEGRATION_CONFIG_FIELDS = {
    [GENERAL_ELECTRIC]: [
        { key: 'manufacturer', label: 'Manufacturer', target: 'default_order' },
        { key: 'po_duplicate_override', label: 'PO Duplicate Override', target: 'default_order' },
    ],
    DISPATCHTRACK: [
        { key: 'company', label: 'Company' },
        { key: 'account', label: 'Account' },
        { key: 'code', label: 'Code' },
        { key: 'baseUrl', label: 'Base URL' },
        { key: 'api_key', label: 'API Key', secret: true, suffix: 'API_KEY' },
        { key: 'client_code', label: 'Client Code' },
        { key: 'modifications', label: 'Modifications', type: 'jsonb' },
    ],
    GRASSHOPPER: [
        { key: 'base_url', label: 'Base URL' },
        { key: 'username', label: 'Username' },
        { key: 'identifier', label: 'Identifier' },
        { key: 'password', label: 'Password', secret: true, suffix: 'PASSWORD' },
        { key: 'skip_cancel', label: 'Skip Cancel', type: 'boolean' },
        { key: 'hubMap', label: 'Hub Map', type: 'jsonb' },
    ],
    FLEETENABLE: [
        { key: 'username', label: 'Username' },
        { key: 'password', label: 'Password', secret: true, suffix: 'PASSWORD' },
        {
            key: 'customer_identifier',
            label: 'Customer Identifier',
            tooltip: 'FleetEnable-issued. Same as shipper code unless they give you a different value.',
        },
        { key: 'shipper_code', label: 'Shipper Code' },
        { key: 'carrier_code', label: 'Carrier Code' },
    ],
    XCELERATOR_AXIS: [
        { key: 'baseUrl', label: 'Base URL' },
        {
            key: 'company',
            label: 'Company',
            tooltip: 'Triggers custom code for more complex customizations for this carrier/shipper combo',
        },
        { key: 'account_no', label: 'Account No' },
        { key: 'xcelerator_username', label: 'Username' },
        { key: 'xcelerator_password', label: 'Password', secret: true, suffix: 'PASSWORD' },
        { key: 'modifications.serviceId', label: 'Service Id', type: 'integer', path: ['modifications', 'serviceId'] },
        { key: 'modifications.vehicleId', label: 'Vehicle Id', type: 'integer', path: ['modifications', 'vehicleId'] },
        {
            key: 'modifications.packageIdMapping.default',
            label: 'Package Id',
            type: 'integer',
            path: ['modifications', 'packageIdMapping', 'default'],
        },
        {
            key: 'modifications.packageIdMapping.pallet',
            label: 'Pallet Id',
            type: 'integer',
            path: ['modifications', 'packageIdMapping', 'pallet'],
        },
        { key: 'manualOnly', label: 'Manual Only', type: 'boolean', tooltip: 'Only applies to the tda company' },
        {
            key: 'modifications.customerScheduled',
            label: 'Customer Scheduled',
            type: 'boolean',
            path: ['modifications', 'customerScheduled'],
        },
        {
            key: 'modifications.fieldMappings',
            label: 'Field Mappings',
            type: 'jsonb',
            path: ['modifications', 'fieldMappings'],
        },
        {
            key: 'modifications.itemMappings',
            label: 'Item Mappings',
            type: 'jsonb',
            path: ['modifications', 'itemMappings'],
        },
        {
            key: 'modifications.conditionalFields',
            label: 'Conditional Fields',
            type: 'jsonb',
            path: ['modifications', 'conditionalFields'],
        },
        {
            key: 'modifications.removeFields',
            label: 'Remove Fields',
            type: 'jsonb',
            path: ['modifications', 'removeFields'],
        },
    ],
    // secret_key is the name of an onward-functions env var (STATIC_SECRETS), so it's set there, not here.
    FRAYT: [
        { key: 'user_id', label: 'User ID' },
        { key: 'client_id', label: 'Client ID' },
        { key: 'vehicle_class', label: 'Vehicle Class' },
        { key: 'needs_pallet_jack', label: 'Needs Pallet Jack', type: 'boolean' },
        { key: 'contract', label: 'Contract' },
        { key: 'service_level', label: 'Service Level', type: 'integer' },
        { key: 'unload_method', label: 'Unload Method' },
        { key: 'pickup_cutoff_local', label: 'Pickup Cutoff (local)', tooltip: 'HH:mm' },
        { key: 'notes.origin', label: 'Origin Notes', path: ['notes', 'origin'] },
    ],
    FORWARD_AIR: [
        { key: 'user', label: 'User' },
        { key: 'password', label: 'Password', secret: true, suffix: 'PASSWORD' },
        { key: 'customer_id', label: 'Customer ID' },
        { key: 'bill_to', label: 'Bill To' },
        { key: 'base_url', label: 'Base URL', tooltip: 'A test-api URL runs in test mode' },
    ],
    // secret_key is the name of an onward-functions env var (STATIC_SECRETS), so it's set there, not here.
    IDLS: [
        { key: 'type', label: 'Type' },
        { key: 'brand', label: 'Brand' },
        { key: 'base_url', label: 'Base URL' },
    ],
    PC_MILER: [
        { key: 'api_key', label: 'API Key', secret: true, suffix: 'PCM_API_KEY' },
        { key: 'version', label: 'PC Miler Version', prefix: 'PCM', tooltip: 'Enter just the version number, e.g. 31' },
        { key: 'order_mileage_enabled', label: 'Use for order mileage calculations', type: 'boolean' },
    ],
    CUSTOMCO: [
        { key: 'user_key', label: 'API User', secret: true, suffix: 'API_USER' },
        { key: 'pass_key', label: 'API Password', secret: true, suffix: 'API_PASS' },
    ],
    // secret_key is the name of an onward-functions env var (STATIC_SECRETS), so it's set there, not here.
    CXT_API: [
        { key: 'client_id', label: 'Username' },
        { key: 'base_url', label: 'Base URL' },
        { key: 'cust_id', label: 'Customer ID' },
        { key: 'order_type_id', label: 'Order Type ID', type: 'integer' },
        { key: 'service_type_id', label: 'Service Type ID', type: 'integer' },
        { key: 'tracking_url', label: 'Tracking URL' },
        { key: 'order_type_map', label: 'Order Type Map', type: 'jsonb' },
    ],
    VAULT: [
        { key: 'base_url', label: 'Base URL' },
        { key: 'username', label: 'Username' },
        { key: 'password', label: 'Password', secret: true, suffix: 'PASSWORD' },
    ],
};
